<script setup>
import { useUserGroupsStore, useUsersStore } from '@/stores'
import GroupMemberCardAdmin from '@core/components/GroupMemberCardAdmin.vue'
import EmailPreviewDialog from '@core/components/EmailPreviewDialog.vue'
import ConfirmDialog from '@core/components/ConfirmDialog.vue'
import moment from 'moment'

const router = useRouter()
const route = useRoute()

const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()
let userGroups = null;
({ userGroups } = storeToRefs(userGroupsStore))

// Guard: must be a group admin/owner (non-restaurant)
const isAuthorized = computed(() => {
  return myUser.value?.primaryGroupType !== 'restaurant'
    && (myUser.value?.primaryGroupPermission === 'owner'
      || myUser.value?.primaryGroupPermission === 'admin'
      || myUser.value?.primaryGroupPermission === 'adminStandard')
})

// Select the first non-restaurant, non-friends group the user administers
const activeGroup = ref(null)
const loading = ref(true)
const searchValue = ref('')
const activeTab = ref('active')
const validInputs = ref(true)
const isConfirmRemindDialogOpen = ref(false)

// RM-331 (11/1): the Accept / Deny links in the "request to join" email open
// this page on the group's Pending tab (?group=<id>&tab=pending&action=accept|deny);
// the admin approves or denies here, signed in. The links themselves act on nothing.
const linkedAction = computed(() => (['accept', 'deny'].includes(route.query.action) ? route.query.action : null))

// Load group data on mount
onMounted(async () => {
  if (!isAuthorized.value) {
    router.replace('/home')
    
    return
  }
  await userGroupsStore.getMyUserGroups(true)
  const manageable = userGroups.value.filter(
    g => g.type !== 'restaurant' && g.type !== 'friends'
  )
  const linked = route.query.group ? manageable.find(g => g.id === route.query.group) : null
  const first = linked || manageable[0]
  if (first) {
    await userGroupsStore.getUserGroupById(first.id)
    activeGroup.value = userGroups.value.find(g => g.id === first.id)
  }
  if (route.query.tab === 'pending' || linkedAction.value) activeTab.value = 'pending'
  loading.value = false
})

// Watch for group selector changes
const selectGroup = async groupId => {
  loading.value = true
  await userGroupsStore.getUserGroupById(groupId)
  activeGroup.value = userGroups.value.find(g => g.id === groupId)
  searchValue.value = ''
  activeTab.value = 'active'
  loading.value = false
}

// Manageable groups (for group selector when user has multiple)
const manageableGroups = computed(() => {
  return (userGroups.value || []).filter(
    g => g.type !== 'restaurant' && g.type !== 'friends'
  )
})

// Member filtering and sorting
function memberSort(a, b) {
  if (a.isNew && !b.isNew) return -1
  if (!a.isNew && b.isNew) return 1
  if (a.firstName && b.firstName) {
    if (a.firstName === b.firstName) return (a.email || '').localeCompare(b.email || '')
    
    return a.firstName.localeCompare(b.firstName)
  }
  if (!a.firstName && !b.firstName) return (a.email || '').localeCompare(b.email || '')
  if (!a.firstName) return 1
  
  return -1
}

const filteredMembers = computed(() => {
  const members = activeGroup.value?.userPermissions || []
  let filtered = members
  if (searchValue.value) {
    const q = searchValue.value.toUpperCase()
    filtered = filtered.filter(m =>
      m?.email?.toUpperCase()?.includes(q)
      || m?.firstName?.toUpperCase()?.includes(q)
      || m?.lastName?.toUpperCase()?.includes(q)
    )
  }
  
  return [...filtered].sort(memberSort)
})

const newMember = computed(() => {
  return activeGroup.value?.userPermissions?.find(m => m.isNew) || null
})

const activeMembers = computed(() =>
  filteredMembers.value.filter(m => m?.emailConfirmed && m.permission !== 'pending' && !m.isNew)
)

const invitedMembers = computed(() =>
  filteredMembers.value.filter(m => !m?.emailConfirmed && m.permission !== 'pending' && !m.isNew)
)

const pendingMembers = computed(() =>
  filteredMembers.value.filter(m => m.permission === 'pending' && !m.isNew)
)

const activeMembershipCount = computed(() => {
  return (activeGroup.value?.userPermissions || []).filter(
    m => m?.emailConfirmed && m.permission !== 'pending' && m.permission !== 'adminStandard' && !m.isNew
  ).length
})

const remainingSeats = computed(() => {
  const diff = (activeGroup.value?.currentNumberOfPaidSeats || 0) - activeMembershipCount.value
  
  return diff > 0 ? diff : 0
})

// Actions
const addMember = () => {
  if (!activeGroup.value || newMember.value) return
  const defaultPermission = activeGroup.value.currentNumberOfPaidSeats === 1
    ? 'adminStandard'
    : 'regular'
  activeGroup.value.userPermissions.push({
    email: '',
    permission: defaultPermission,
    isNew: true,
    invitedBy: myUser.value.email,
  })
}

const remindAllInvited = () => {
  for (const member of invitedMembers.value) {
    member.sendInvite = true
    member.invitedBy = myUser.value.email
  }

  // Set the store's adminEditingUserGroupMembers so save works
  userGroupsStore.adminEditingUserGroupMembers = activeGroup.value
  userGroupsStore.saveAdminEditingUserGroupMembers()
}

const exportMembers = () => {
  const members = activeGroup.value?.userPermissions || []
  const header = 'First Name,Last Name,Email,Role,Registered'
  const rows = members.map(m => {
    const fields = [
      m.firstName || '',
      m.lastName || '',
      m.email || '',
      m.permission || '',
      m.emailConfirmed ? 'Yes' : 'No',
    ]
    
    return fields.map(f => {
      const s = String(f)
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`
      }
      
      return s
    }).join(',')
  })

  const csv = [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${activeGroup.value?.name || 'group'}_members_${moment().format('YYYY-MM-DD')}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Keep the store's editing reference in sync so GroupMemberCardAdmin's save works
watch(activeGroup, g => {
  if (g) {
    userGroupsStore.adminEditingUserGroupMembers = g
  }
}, { immediate: true })

// Sync back from store after saves (store creates a new object on save response)
const adminEditingRef = computed(() => userGroupsStore.adminEditingUserGroupMembers)
watch(adminEditingRef, updated => {
  if (updated && activeGroup.value && updated.id === activeGroup.value.id) {
    activeGroup.value = updated
  }
})

onBeforeUnmount(() => {
  userGroupsStore.adminEditingUserGroupMembers = null
})
</script>

<template>
  <div v-if="isAuthorized">
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmRemindDialogOpen"
      confirmation-msg="Send email reminders to all members who have not registered?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="remindAllInvited"
    />

    <div class="content-wrapper mt-6 mb-n2">
      <div class="back-button-container">
        <RouterLink
          :to="myUser?.profileId ? `/m/${myUser.profileId}` : '/home'"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back</span>
        </RouterLink>
      </div>
    </div>

    <div class="content-wrapper mt-4">
      <!-- Loading state -->
      <div
        v-if="loading"
        class="d-flex justify-center py-16"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <!-- No groups -->
      <div
        v-else-if="!activeGroup"
        class="text-center py-16"
      >
        <div class="text-h6 text-medium-emphasis mb-2">
          No groups to manage
        </div>
        <div class="text-body-2 text-medium-emphasis">
          You don't have admin access to any groups.
        </div>
      </div>

      <!-- Main content -->
      <template v-else>
        <!-- Page header -->
        <div class="gm-header mb-6">
          <div class="gm-header-top">
            <div>
              <!-- Group selector (if multiple groups) -->
              <VSelect
                v-if="manageableGroups.length > 1"
                :model-value="activeGroup?.id"
                :items="manageableGroups.map(g => ({ title: g.name, value: g.id }))"
                variant="plain"
                density="compact"
                hide-details
                class="gm-group-select"
                @update:model-value="selectGroup"
              />
              <h1
                v-else
                class="gm-title"
              >
                {{ activeGroup.name }}
              </h1>
            </div>
            <div class="gm-header-actions">
              <VBtn
                variant="text"
                color="default"
                size="small"
                icon="mdi-file-export"
                title="Export CSV"
                @click="exportMembers"
              />
              <VBtn
                v-if="!newMember"
                color="primary"
                size="small"
                prepend-icon="mdi-plus"
                @click="addMember"
              >
                Add Member
              </VBtn>
            </div>
          </div>

          <!-- Seats summary -->
          <div
            v-if="activeGroup.type === 'enterprise'"
            class="gm-seats"
          >
            <div class="gm-seats-item">
              <span class="gm-seats-value">{{ activeGroup.currentNumberOfPaidSeats || 0 }}</span>
              <span class="gm-seats-label">Paid Seats</span>
            </div>
            <div class="gm-seats-divider" />
            <div class="gm-seats-item">
              <span class="gm-seats-value">{{ activeMembershipCount }}</span>
              <span class="gm-seats-label">Active</span>
            </div>
            <div class="gm-seats-divider" />
            <div class="gm-seats-item">
              <span class="gm-seats-value">{{ remainingSeats }}</span>
              <span class="gm-seats-label">Remaining</span>
            </div>
          </div>
        </div>

        <!-- New member card (appears above tabs when adding) -->
        <GroupMemberCardAdmin
          v-if="newMember"
          class="mb-4"
          style="width: 100%;"
          :member="newMember"
          :user-group="activeGroup"
          :valid-inputs="validInputs"
          is-editing
          @update:valid-inputs="v => validInputs = v"
        />

        <!-- Search -->
        <VTextField
          v-if="(activeGroup?.userPermissions?.length || 0) > 3"
          v-model="searchValue"
          density="compact"
          variant="solo"
          label="Search by name or email"
          append-inner-icon="mdi-magnify"
          single-line
          hide-details
          clearable
          class="mb-4"
        />

        <!-- Tabs -->
        <VTabs
          v-model="activeTab"
          class="mb-4"
        >
          <VTab value="active">
            Active
            <VBadge
              v-if="activeMembers.length"
              :content="activeMembers.length"
              inline
              class="ml-2"
            />
          </VTab>
          <VTab value="invited">
            Invited
            <VBadge
              v-if="invitedMembers.length"
              :content="invitedMembers.length"
              inline
              class="ml-2"
            />
          </VTab>
          <VTab value="pending">
            Pending
            <VBadge
              v-if="pendingMembers.length"
              :content="pendingMembers.length"
              color="warning"
              inline
              class="ml-2"
            />
          </VTab>
        </VTabs>

        <!-- Tab content -->
        <VWindow v-model="activeTab">
          <!-- Active members -->
          <VWindowItem value="active">
            <div
              v-if="activeMembers.length === 0"
              class="gm-empty"
            >
              No active members{{ searchValue ? ' matching your search' : '' }}
            </div>
            <GroupMemberCardAdmin
              v-for="member in activeMembers"
              :key="member.email"
              class="mb-3"
              :member="member"
              :user-group="activeGroup"
              :valid-inputs="validInputs"
              @update:valid-inputs="v => validInputs = v"
            />
          </VWindowItem>

          <!-- Invited (not yet registered) -->
          <VWindowItem value="invited">
            <div
              v-if="invitedMembers.length > 1 && !searchValue"
              class="d-flex justify-center mb-4"
            >
              <VBtn
                variant="outlined"
                size="small"
                prepend-icon="mdi-email-outline"
                @click="isConfirmRemindDialogOpen = true"
              >
                Remind All
              </VBtn>
            </div>
            <div
              v-if="invitedMembers.length === 0"
              class="gm-empty"
            >
              All invited members have registered
            </div>
            <GroupMemberCardAdmin
              v-for="member in invitedMembers"
              :key="member.email"
              class="mb-3"
              :member="member"
              :user-group="activeGroup"
              :valid-inputs="validInputs"
              @update:valid-inputs="v => validInputs = v"
            />
          </VWindowItem>

          <!-- Pending approval -->
          <VWindowItem value="pending">
            <div
              v-if="pendingMembers.length === 0"
              class="gm-empty"
            >
              No members awaiting approval
            </div>
            <GroupMemberCardAdmin
              v-for="member in pendingMembers"
              :key="member.email"
              class="mb-3"
              :member="member"
              :user-group="activeGroup"
              :valid-inputs="validInputs"
              @update:valid-inputs="v => validInputs = v"
            />
          </VWindowItem>
        </VWindow>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content-wrapper {
  max-width: 680px;
  margin-inline: auto;
  padding-inline: 16px;
}

.back-button-container {
  .back-button {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    color: var(--ds-text-secondary, rgba(0, 0, 0, 0.6));
    text-decoration: none;

    &:hover {
      color: var(--ds-text-primary, rgba(0, 0, 0, 0.87));
    }
  }
}

/* Header */
.gm-header {
  border-block-end: 1px solid rgba(0, 0, 0, 0.08);
  padding-block-end: 20px;
}

.gm-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.gm-title {
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.gm-group-select {
  :deep(.v-field__input) {
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.02em;
    padding: 0;
    min-block-size: auto;
  }
}

.gm-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Seats summary */
.gm-seats {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-block-start: 16px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
}

.gm-seats-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gm-seats-value {
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
}

.gm-seats-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}

.gm-seats-divider {
  inline-size: 1px;
  block-size: 28px;
  background: rgba(0, 0, 0, 0.1);
}

/* Empty state */
.gm-empty {
  padding: 40px 16px;
  text-align: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
